import {
  CascaderField,
  Form,
  SelectField,
  Space,
  SubmitButton,
  TextInputField,
} from '@/components';
import { useOrganizationOptionsRecursive } from '@/features/tenant-manager/organization/_hooks/organization';
import { NEST_ITEM_CONNECTOR } from '@/utils';
import { type FC, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import type { OrganizationTemplateFormSchema } from '../_models/organization-template';

type FormValues = z.infer<typeof OrganizationTemplateFormSchema>;

type OrgOption = {
  label: string;
  value: string | number;
  children?: OrgOption[];
};

type Props = {
  onSubmit: (values: FormValues) => void;
};

export const OrganizationTemplateForm: FC<Props> = ({ onSubmit }) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    formState: { errors, isDirty, isSubmitting, isSubmitSuccessful },
  } = useFormContext<Partial<FormValues>, unknown, FormValues>();

  const organizationsOptions = useOrganizationOptionsRecursive();

  const flatOrganizationsOptions = useMemo<
    { label: string; value: string }[]
  >(() => {
    if (!organizationsOptions.data?.length) {
      return [];
    }
    return organizationsOptions.data.flatMap((item) => {
      const flattenOrganizations = (
        current: OrgOption,
        parentLabels: string[] = [],
      ): { label: string; value: string }[] => {
        const currentLabelPath = [...parentLabels, current.label];
        const currentResult = [
          {
            label: currentLabelPath.join(NEST_ITEM_CONNECTOR),
            value: current.value.toString(),
          },
        ];
        const childrenResults =
          current.children && current.children.length > 0
            ? current.children.flatMap((child) =>
                flattenOrganizations(child, currentLabelPath),
              )
            : [];
        return [...currentResult, ...childrenResults];
      };

      return flattenOrganizations(item as OrgOption);
    });
  }, [organizationsOptions.data]);

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <TextInputField
        name="name"
        autocompleteKey="organization.template.name"
        label={t('organization-template.name')}
        control={control}
        errors={errors}
        required
      />
      <CascaderField
        name="userOrganizationIds"
        label={t('organization-template.user-organizations')}
        control={control}
        errors={errors}
        options={organizationsOptions.data}
        loading={organizationsOptions.isPending}
        required
        multiple
        allowClear
      />
      <SelectField
        name="organizationIds"
        label={t('organization-template.template-organizations')}
        control={control}
        errors={errors}
        options={flatOrganizationsOptions}
        loading={organizationsOptions.isPending}
        required
        mode="multiple"
        allowClear
      />
      <Space>
        <SubmitButton loading={isSubmitting} disabled={!isDirty} />
      </Space>
    </Form>
  );
};
